import { type FC, lazy, Suspense } from "react"

import { type RichContentDocument } from "@forento/rich-content"
import type { LinkPreviewMetadata, RichContentValue } from "@forento/ui/components/RichContentEditor"
import { startVideoUpload } from "@forento/ui/utilities/video"

import trpc from "~/utilities/trpc"

const SharedRichContentEditor = lazy(() => import("@forento/ui/components/RichContentEditor"))

type Props = {
	placeholder: string
	initialValue: RichContentDocument | null
	setValue(value: RichContentValue): void
	className?: string
}
const RichContentEditor: FC<Props> = props => (
	<Suspense
		fallback={
			<div className="border-component-border bg-component-background min-h-96 w-full animate-pulse rounded-xl border" />
		}
	>
		<SharedRichContentEditor
			{...props}
			uploadImage={uploadImage}
			uploadVideo={uploadVideo}
			fetchLinkPreview={fetchLinkPreview}
		/>
	</Suspense>
)

async function uploadImage(file: File): Promise<string> {
	const formData = new FormData()
	formData.set("image", file)
	const response = await fetch("/api/rich-content/image-file", { method: "POST", body: formData })
	const result: unknown = await response.json()
	if (!isRecord(result) || result.success !== 1 || !isRecord(result.file) || typeof result.file.url !== "string") {
		throw new Error("Image upload failed")
	}
	return result.file.url
}

async function uploadVideo(file: File, onProgress: (progress: number) => void): Promise<string> {
	const { bunnyVideoId } = await trpc.product.initiateRichContentVideoUpload.mutate()
	const videoUpload = await trpc.video.initializeUpload.query({ bunnyVideoId })

	return new Promise((resolve, reject) => {
		void startVideoUpload({
			file,
			bunnyVideoId,
			libraryId: videoUpload.libraryId,
			signature: videoUpload.signature,
			onProgress(progress) {
				if (progress.status === "uploading") onProgress(progress.progress)
				if (progress.status === "completed") resolve(bunnyVideoId)
				if (progress.status === "failed") reject(new Error("Video upload failed"))
			},
		})
	})
}

async function fetchLinkPreview(url: string): Promise<LinkPreviewMetadata | null> {
	const response = await fetch(`/api/rich-content/link?url=${encodeURIComponent(url)}`)
	const result: unknown = await response.json()
	if (!isRecord(result) || result.success !== 1 || typeof result.link !== "string" || !isRecord(result.meta)) {
		return null
	}
	const imageUrl =
		isRecord(result.meta.image) && typeof result.meta.image.url === "string" ? result.meta.image.url : null
	return {
		href: result.link,
		title: typeof result.meta.title === "string" ? result.meta.title : "",
		description: typeof result.meta.description === "string" ? result.meta.description : "",
		imageUrl,
	}
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null
}

export default RichContentEditor
